import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Badge,
} from "@mui/material";
import {
  FaArrowLeft,
  FaSyncAlt,
  FaEdit,
  FaSave,
  FaDollarSign,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimes,
  FaWallet,
} from "react-icons/fa";
import { MdAccountBalanceWallet, MdShowChart, MdPending } from "react-icons/md";
import { BiTransfer } from "react-icons/bi";
import { RiExchangeDollarFill } from "react-icons/ri";
import { TbWorld } from "react-icons/tb";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "../components/NotificationProvider";
import {
  fetchAuthorizedTraders,
  fetchTraderDetail,
  updateTraderCommissions,
  fetchCryptoDeposits,
  fetchBalanceHistory,
  resetTraderDetail,
  clearTraderKassaError,
  fetchPendingTraderDeposits,
  approveTraderDeposit,
  rejectTraderDeposit,
  fetchPendingTraderWithdrawals,
  approveTraderWithdrawal,
  rejectTraderWithdrawal,
  updateTraderTrcAddress,
  getTraderTrcAddress,
  fetchAdminTraderWithdrawals,
} from "../store";
import "./TraderKassaManagementView.css";
import aznGreenIcon from "../assets/images/azn-green.png";
import aznRedIcon from "../assets/images/azn-red.png";

const TraderKassaManagementView = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const {
    authorizedTraders = [],
    traderDetail = null,
    cryptoDeposits = [],
    withdrawals = [],
    balanceHistory = [],
    pendingDeposits = [],
    pendingWithdrawals = [],
    isLoading = false,
    error = null,
  } = useSelector((state) => state.traderKassaManagement || {});

  const [viewMode, setViewMode] = useState("traders"); // "traders" | "trader-detail" | "pending-deposits" | "pending-withdrawals"
  const [selectedTraderName, setSelectedTraderName] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [depositCommission, setDepositCommission] = useState("");
  const [withdrawalCommission, setWithdrawalCommission] = useState("");

  // TRC Address Management
  const [isEditingTRC, setIsEditingTRC] = useState(false);
  const [trcAddress, setTrcAddress] = useState("");
  const [trcNetwork, setTrcNetwork] = useState("TRC20");

  // Approve Deposit Modal
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [exchangeRate, setExchangeRate] = useState("");
  const [approvalNotes, setApprovalNotes] = useState("");

  // Reject Deposit Modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Approve Withdrawal Modal
  const [approveWithdrawalModalOpen, setApproveWithdrawalModalOpen] =
    useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [withdrawalExchangeRate, setWithdrawalExchangeRate] = useState("");
  const [withdrawalNotes, setWithdrawalNotes] = useState("");
  const [withdrawalTxId, setWithdrawalTxId] = useState("");

  // Reject Withdrawal Modal
  const [rejectWithdrawalModalOpen, setRejectWithdrawalModalOpen] =
    useState(false);
  const [withdrawalRejectReason, setWithdrawalRejectReason] = useState("");

  useEffect(() => {
    if (user && user.user && user.user.role === "admin") {
      console.log("👤 Current User:", user.user);
      console.log(
        "🔑 Trader Kassa Management Config:",
        user.user.traderKassaManagement
      );
      console.log("✅ Enabled:", user.user.traderKassaManagement?.enabled);
      console.log(
        "📋 Authorized Traders:",
        user.user.traderKassaManagement?.authorizedTraders
      );
      fetchTraders();
      checkPendingDeposits();
      checkPendingWithdrawals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkPendingDeposits = async () => {
    try {
      const result = await dispatch(fetchPendingTraderDeposits()).unwrap();
      const count = result.deposits?.length || 0;
      console.log("🔔 Pending Deposits Count:", count);

      if (count > 0) {
        showNotification(
          "Pending Deposits",
          "warning",
          `You have ${count} pending deposit${
            count > 1 ? "s" : ""
          } waiting for approval`
        );
      }
    } catch (error) {
      console.error("❌ Error checking pending deposits:", error);
    }
  };

  const checkPendingWithdrawals = async () => {
    try {
      const result = await dispatch(fetchPendingTraderWithdrawals()).unwrap();
      const count = result.withdrawals?.length || 0;
      console.log("🔔 Pending Withdrawals Count:", count);

      if (count > 0) {
        showNotification(
          "Pending Withdrawals",
          "warning",
          `You have ${count} pending withdrawal${
            count > 1 ? "s" : ""
          } waiting for approval`
        );
      }
    } catch (error) {
      console.error("❌ Error checking pending withdrawals:", error);
    }
  };

  useEffect(() => {
    if (error) {
      showNotification("Error", "error", error);
      dispatch(clearTraderKassaError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (traderDetail) {
      console.log("🔧 Trader Detail:", traderDetail);
      console.log("💳 Crypto Wallet:", traderDetail.cryptoWallet);
      setDepositCommission(traderDetail.depositCommission || 0);
      setWithdrawalCommission(traderDetail.withdrawalCommission || 0);
      // TRC address comes from cryptoWallet.address in the response
      setTrcAddress(traderDetail.cryptoWallet?.address || "");
      setTrcNetwork(traderDetail.cryptoWallet?.network || "TRC20");
    }
  }, [traderDetail]);

  const fetchTraders = async () => {
    try {
      const result = await dispatch(fetchAuthorizedTraders()).unwrap();
      console.log("🔍 Fetched Authorized Traders:", result);
      console.log(
        "🔍 Number of authorized traders:",
        result.traders?.length || 0
      );
      console.log(
        "🔍 Trader names:",
        result.traders?.map((t) => t.traderName)
      );
    } catch (error) {
      console.error("❌ Error fetching traders:", error);
    }
  };

  const handleTraderSelect = async (traderName) => {
    setSelectedTraderName(traderName);
    setViewMode("trader-detail");
    try {
      await dispatch(fetchTraderDetail(traderName)).unwrap();
      await dispatch(
        fetchCryptoDeposits({ traderName, limit: 50, page: 1 })
      ).unwrap();
      await dispatch(
        fetchAdminTraderWithdrawals({ traderName, limit: 50, page: 1 })
      ).unwrap();
      await dispatch(
        fetchBalanceHistory({ traderName, limit: 50, page: 1 })
      ).unwrap();
      // Also fetch pending withdrawals for badge count
      await dispatch(fetchPendingTraderWithdrawals()).unwrap();
    } catch (error) {
      console.error("Error fetching trader details:", error);
    }
  };

  const handleBackToList = async () => {
    setSelectedTraderName(null);
    setViewMode("traders");
    setActiveTab(0);
    setIsEditingCommission(false);
    setIsEditingTRC(false);
    dispatch(resetTraderDetail());
    // Refresh trader list and pending deposits/withdrawals when returning
    await fetchTraders();
    await dispatch(fetchPendingTraderDeposits())
      .unwrap()
      .catch(() => {});
    await dispatch(fetchPendingTraderWithdrawals())
      .unwrap()
      .catch(() => {});
  };

  const handleViewPendingDeposits = async () => {
    setViewMode("pending-deposits");
    try {
      await dispatch(fetchPendingTraderDeposits()).unwrap();
    } catch (error) {
      console.error("Error fetching pending deposits:", error);
    }
  };

  const handleBackToPendingList = async () => {
    setViewMode("traders");
    // Refresh trader list and pending deposits/withdrawals when returning
    await fetchTraders();
    await dispatch(fetchPendingTraderDeposits())
      .unwrap()
      .catch(() => {});
    await dispatch(fetchPendingTraderWithdrawals())
      .unwrap()
      .catch(() => {});
  };

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditCommission = () => {
    setIsEditingCommission(true);
  };

  const handleSaveCommission = async () => {
    if (!selectedTraderName) return;

    try {
      await dispatch(
        updateTraderCommissions({
          traderName: selectedTraderName,
          depositCommission: parseFloat(depositCommission),
          withdrawalCommission: parseFloat(withdrawalCommission),
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        "Commissions updated successfully"
      );
      setIsEditingCommission(false);

      // Refresh trader detail to get updated data
      await dispatch(fetchTraderDetail(selectedTraderName)).unwrap();
    } catch (error) {
      console.error("Error updating commissions:", error);
      showNotification("Error", "error", "Failed to update commissions");
    }
  };

  const handleEditTRC = () => {
    setIsEditingTRC(true);
  };

  const handleSaveTRC = async () => {
    if (!selectedTraderName) return;

    try {
      await dispatch(
        updateTraderTrcAddress({
          traderName: selectedTraderName,
          trcWalletAddress: trcAddress,
          trcWalletNetwork: trcNetwork,
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        "TRC address updated successfully"
      );
      setIsEditingTRC(false);

      // Refresh trader detail to get updated data
      await dispatch(fetchTraderDetail(selectedTraderName)).unwrap();
    } catch (error) {
      console.error("Error updating TRC address:", error);
      showNotification("Error", "error", "Failed to update TRC address");
    }
  };

  const handleApproveDeposit = (deposit) => {
    setSelectedDeposit(deposit);
    setExchangeRate("1.7"); // Default exchange rate
    setApprovalNotes("");
    setApproveModalOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedDeposit || !exchangeRate) {
      showNotification("Error", "error", "Please enter exchange rate");
      return;
    }

    try {
      await dispatch(
        approveTraderDeposit({
          depositId: selectedDeposit._id,
          exchangeRate: parseFloat(exchangeRate),
          notes: approvalNotes,
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        `Deposit approved! ${
          selectedDeposit.amount
        } USDT x ${exchangeRate} = ${(
          selectedDeposit.amount * parseFloat(exchangeRate)
        ).toFixed(2)} AZN`
      );
      setApproveModalOpen(false);
      setSelectedDeposit(null);
      setExchangeRate("");
      setApprovalNotes("");

      // Refresh pending deposits
      await dispatch(fetchPendingTraderDeposits()).unwrap();
    } catch (error) {
      console.error("Error approving deposit:", error);
      showNotification("Error", "error", "Failed to approve deposit");
    }
  };

  const handleRejectDeposit = (deposit) => {
    setSelectedDeposit(deposit);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedDeposit) {
      showNotification("Error", "error", "No deposit selected");
      return;
    }

    try {
      await dispatch(
        rejectTraderDeposit({
          depositId: selectedDeposit._id,
          reason: rejectReason,
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        `Deposit rejected for ${selectedDeposit.traderName}`
      );
      setRejectModalOpen(false);
      setSelectedDeposit(null);
      setRejectReason("");

      // Refresh pending deposits
      await dispatch(fetchPendingTraderDeposits()).unwrap();
    } catch (error) {
      console.error("Error rejecting deposit:", error);
      showNotification("Error", "error", "Failed to reject deposit");
    }
  };

  // Withdrawal Handlers
  const handleViewPendingWithdrawals = async () => {
    setViewMode("pending-withdrawals");
    try {
      await dispatch(fetchPendingTraderWithdrawals()).unwrap();
    } catch (error) {
      console.error("Error fetching pending withdrawals:", error);
    }
  };

  const handleApproveWithdrawal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setWithdrawalExchangeRate("1.7"); // Default exchange rate
    setWithdrawalNotes("");
    setWithdrawalTxId("");
    setApproveWithdrawalModalOpen(true);
  };

  const handleApproveWithdrawalConfirm = async () => {
    if (!selectedWithdrawal || !withdrawalExchangeRate) {
      showNotification("Error", "error", "Please enter exchange rate");
      return;
    }

    try {
      await dispatch(
        approveTraderWithdrawal({
          withdrawalId: selectedWithdrawal._id,
          exchangeRate: parseFloat(withdrawalExchangeRate),
          notes: withdrawalNotes,
          txId: withdrawalTxId,
        })
      ).unwrap();

      // Calculate amounts for notification
      const currency = selectedWithdrawal.currency;
      const amount = selectedWithdrawal.amount;
      const rate = parseFloat(withdrawalExchangeRate);

      let message = "";
      if (currency === "USDT") {
        const aznAmount = (amount * rate).toFixed(2);
        message = `Withdrawal approved! ${amount} USDT x ${rate} = ${aznAmount} AZN deducted`;
      } else {
        const usdtAmount = (amount / rate).toFixed(2);
        message = `Withdrawal approved! ${amount} AZN ÷ ${rate} = ${usdtAmount} USDT sent`;
      }

      showNotification("Success", "success", message);
      setApproveWithdrawalModalOpen(false);
      setSelectedWithdrawal(null);
      setWithdrawalExchangeRate("");
      setWithdrawalNotes("");
      setWithdrawalTxId("");

      // Refresh pending withdrawals
      await dispatch(fetchPendingTraderWithdrawals()).unwrap();
    } catch (error) {
      console.error("Error approving withdrawal:", error);
      showNotification("Error", "error", "Failed to approve withdrawal");
    }
  };

  const handleRejectWithdrawal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setWithdrawalRejectReason("");
    setRejectWithdrawalModalOpen(true);
  };

  const handleRejectWithdrawalConfirm = async () => {
    if (!selectedWithdrawal) {
      showNotification("Error", "error", "No withdrawal selected");
      return;
    }

    try {
      await dispatch(
        rejectTraderWithdrawal({
          withdrawalId: selectedWithdrawal._id,
          reason: withdrawalRejectReason,
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        `Withdrawal rejected for ${selectedWithdrawal.traderName}`
      );
      setRejectWithdrawalModalOpen(false);
      setSelectedWithdrawal(null);
      setWithdrawalRejectReason("");

      // Refresh pending withdrawals
      await dispatch(fetchPendingTraderWithdrawals()).unwrap();
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
      showNotification("Error", "error", "Failed to reject withdrawal");
    }
  };

  const handleRefresh = async () => {
    if (selectedTraderName) {
      try {
        await dispatch(fetchTraderDetail(selectedTraderName)).unwrap();
        if (activeTab === 0) {
          await dispatch(
            fetchCryptoDeposits({
              traderName: selectedTraderName,
              limit: 50,
              page: 1,
            })
          ).unwrap();
        } else if (activeTab === 1) {
          await dispatch(
            fetchBalanceHistory({
              traderName: selectedTraderName,
              limit: 50,
              page: 1,
            })
          ).unwrap();
        }
      } catch (error) {
        console.error("Error refreshing data:", error);
      }
    } else {
      fetchTraders();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "#4CAF50"; // Green - Approved
      case "pending":
      case "pending_admin_approval":
        return "#FF9800"; // Orange - Waiting
      case "canceled":
      case "cancelled":
        return "#757575"; // Grey - Canceled
      case "rejected":
        return "#F44336"; // Red - Rejected
      default:
        return "#757575"; // Grey - Unknown
    }
  };

  const renderStatusCell = (cellData) => {
    const status = cellData.value;
    return (
      <Chip
        label={status.toUpperCase()}
        sx={{
          backgroundColor: getStatusColor(status),
          color: "white",
          fontWeight: "bold",
          fontSize: "1.1rem",
          height: "36px",
          padding: "8px 16px",
        }}
      />
    );
  };

  const renderAmountCell = (cellData) => {
    const row = cellData.data;
    const isWithdrawal = row.type === "withdrawal";
    const isCanceled = row.status === "canceled";

    if (isWithdrawal) {
      // Withdrawal display
      const originalCurrency = row.currency;
      const displayAmount = cellData.value;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {originalCurrency === "USDT" ? (
            <FaDollarSign
              size={18}
              color={isCanceled ? "#757575" : "#F44336"}
            />
          ) : (
            <img
              src={aznRedIcon}
              alt="AZN"
              style={{
                width: "18px",
                height: "18px",
                filter: isCanceled ? "grayscale(100%)" : "none",
                opacity: isCanceled ? 0.6 : 1,
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              color: isCanceled ? "#757575" : "#F44336",
              fontSize: "1.4rem",
            }}
          >
            -{displayAmount.toFixed(2)} {originalCurrency}
          </Typography>
          {row.usdtAmount && row.aznAmount && (
            <Typography
              variant="body2"
              sx={{
                color: isCanceled ? "#757575" : "#FF9800",
                fontSize: "1.3rem",
                fontWeight: "500",
                marginLeft: "4px",
              }}
            >
              (
              {originalCurrency === "USDT"
                ? `${row.aznAmount.toFixed(2)} AZN`
                : `${row.usdtAmount.toFixed(2)} USDT`}
              )
            </Typography>
          )}
        </Box>
      );
    }

    // Deposit display
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <FaDollarSign size={18} color={isCanceled ? "#757575" : "#4CAF50"} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: isCanceled ? "#757575" : "#E0E6ED",
            fontSize: "1.4rem",
          }}
        >
          {cellData.value.toFixed(2)} USDT
        </Typography>
        {row.aznAmount && (
          <Typography
            variant="body2"
            sx={{
              color: isCanceled ? "#757575" : "#FF9800",
              fontSize: "1.3rem",
              fontWeight: "500",
              marginLeft: "4px",
            }}
          >
            ( {row.aznAmount.toFixed(2)} AZN )
          </Typography>
        )}
      </Box>
    );
  };

  const renderHistoryAmountCell = (cellData) => {
    const row = cellData.data;
    const amount = cellData.value;
    const isPositive = amount >= 0;
    const isWithdrawal = row.type === "withdrawal";

    // Check if this is a withdrawal with original currency info
    const originalCurrency = row.metadata?.originalCurrency;
    const originalAmount = row.metadata?.originalAmount;
    const usdtAmount = row.metadata?.usdtAmount;
    const aznAmount = row.metadata?.aznAmount;

    // For withdrawals, show based on original currency
    if (isWithdrawal && originalCurrency) {
      if (originalCurrency === "AZN") {
        // AZN withdrawal: Show AZN icon + amount (USDT equivalent)
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <img
              src={aznRedIcon}
              alt="AZN"
              style={{ width: "20px", height: "20px" }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: "#F44336",
                fontSize: "1.4rem",
              }}
            >
              -{Math.abs(aznAmount || originalAmount).toFixed(2)} AZN
              <span
                style={{
                  color: "#FF9800",
                  fontSize: "1.2rem",
                  marginLeft: "4px",
                }}
              >
                ({Math.abs(usdtAmount).toFixed(2)} USDT)
              </span>
            </Typography>
          </Box>
        );
      } else if (originalCurrency === "USDT") {
        // USDT withdrawal: Show red dollar + USDT amount (AZN equivalent)
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <FaDollarSign size={20} color="#F44336" />
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: "#F44336",
                fontSize: "1.4rem",
              }}
            >
              -{Math.abs(usdtAmount || originalAmount).toFixed(2)} USDT
              <span
                style={{
                  color: "#FF9800",
                  fontSize: "1.2rem",
                  marginLeft: "4px",
                }}
              >
                ({Math.abs(aznAmount).toFixed(2)} AZN)
              </span>
            </Typography>
          </Box>
        );
      }
    }

    // Default rendering for deposits and other transactions
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <img
          src={isPositive ? aznGreenIcon : aznRedIcon}
          alt="AZN"
          style={{ width: "20px", height: "20px" }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: isPositive ? "#4CAF50" : "#F44336",
            fontSize: "1.4rem",
          }}
        >
          {isPositive ? "+" : ""}
          {amount.toFixed(2)} AZN
        </Typography>
      </Box>
    );
  };

  const renderHistoryTypeCell = (cellData) => {
    const type = cellData.value;

    // Map backend types to display labels and colors
    const typeConfig = {
      deposit: { label: "DEPOSIT", color: "#4CAF50" },
      withdrawal: { label: "WITHDRAWAL", color: "#F44336" },
      crypto_deposit: { label: "CRYPTO DEPOSIT", color: "#4CAF50" },
      crypto_withdrawal: { label: "CRYPTO WITHDRAWAL", color: "#F44336" },
    };

    const config = typeConfig[type] || {
      label: type?.toUpperCase() || "UNKNOWN",
      color: "#757575",
    };

    return (
      <Chip
        label={config.label}
        sx={{
          backgroundColor: config.color,
          color: "white",
          fontWeight: "bold",
          fontSize: "1.1rem",
          height: "36px",
          padding: "8px 16px",
        }}
      />
    );
  };

  const renderExchangeRateCell = (cellData) => {
    const rate = cellData.value;
    if (!rate)
      return <span style={{ color: "#B0BEC5", fontSize: "1.4rem" }}>-</span>;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: "600", color: "#E0E6ED", fontSize: "1.5rem" }}
        >
          {rate.toFixed(5)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#B0BEC5", fontSize: "0.9rem" }}
        >
          ₼/USD
        </Typography>
      </Box>
    );
  };

  // Trader List View
  if (viewMode === "traders") {
    return (
      <div className="trader-kassa-management-view">
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ color: "#E0E6ED", mb: 1 }}>
              Select a Trader to Manage
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#B0BEC5" }}>
              Choose from your authorized traders
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Badge
              badgeContent={pendingDeposits.length}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  minWidth: "28px",
                  height: "28px",
                  borderRadius: "14px",
                  animation:
                    pendingDeposits.length > 0 ? "pulse 2s infinite" : "none",
                  "@keyframes pulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)",
                    },
                    "50%": {
                      transform: "scale(1.1)",
                      boxShadow: "0 0 0 8px rgba(244, 67, 54, 0)",
                    },
                  },
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={<MdPending size={22} />}
                onClick={handleViewPendingDeposits}
                sx={{
                  backgroundColor:
                    pendingDeposits.length > 0 ? "#F44336" : "#FF9800",
                  "&:hover": {
                    backgroundColor:
                      pendingDeposits.length > 0 ? "#D32F2F" : "#F57C00",
                    transform: "translateY(-2px)",
                    boxShadow:
                      pendingDeposits.length > 0
                        ? "0 6px 20px rgba(244, 67, 54, 0.5)"
                        : "0 6px 20px rgba(255, 152, 0, 0.4)",
                  },
                  px: 3,
                  py: 1.5,
                  fontSize: "1.3rem",
                  fontWeight: "bold",
                  borderRadius: "12px",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                }}
              >
                Pending Deposits
              </Button>
            </Badge>

            <Badge
              badgeContent={pendingWithdrawals.length}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  minWidth: "28px",
                  height: "28px",
                  borderRadius: "14px",
                  animation:
                    pendingWithdrawals.length > 0
                      ? "pulse 2s infinite"
                      : "none",
                  "@keyframes pulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)",
                    },
                    "50%": {
                      transform: "scale(1.1)",
                      boxShadow: "0 0 0 8px rgba(244, 67, 54, 0)",
                    },
                  },
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={<BiTransfer size={22} />}
                onClick={handleViewPendingWithdrawals}
                sx={{
                  backgroundColor:
                    pendingWithdrawals.length > 0 ? "#F44336" : "#9C27B0",
                  "&:hover": {
                    backgroundColor:
                      pendingWithdrawals.length > 0 ? "#D32F2F" : "#7B1FA2",
                    transform: "translateY(-2px)",
                    boxShadow:
                      pendingWithdrawals.length > 0
                        ? "0 6px 20px rgba(244, 67, 54, 0.5)"
                        : "0 6px 20px rgba(156, 39, 176, 0.4)",
                  },
                  px: 3,
                  py: 1.5,
                  fontSize: "1.3rem",
                  fontWeight: "bold",
                  borderRadius: "12px",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                }}
              >
                Pending Withdrawals
              </Button>
            </Badge>
          </Box>
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <Typography variant="h6" sx={{ color: "#B0BEC5" }}>
              Loading traders...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {authorizedTraders.map((trader) => (
              <Grid item xs={12} sm={6} md={4} key={trader.traderName}>
                <Card
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 40px rgba(102, 126, 234, 0.5)",
                    },
                    minHeight: "280px",
                  }}
                  onClick={() => handleTraderSelect(trader.traderName)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <MdAccountBalanceWallet size={48} color="#ffffff" />
                      <Chip
                        label={trader.traderVersion}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.3)",
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "1.3rem",
                          height: "32px",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ color: "#ffffff", fontWeight: "bold", mb: 2 }}
                    >
                      {trader.traderName}
                    </Typography>

                    {/* Balance Section - Made Bigger */}
                    <Box
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.15)",
                        borderRadius: "12px",
                        padding: "16px",
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          fontSize: "1.5rem",
                          mb: 0.5,
                          fontWeight: "500",
                        }}
                      >
                        Balance
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "3.2rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {trader.balance?.toFixed(2) || "0.00"} ₼
                      </Typography>
                    </Box>

                    {/* Commission Rates Section */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderRadius: "10px",
                        padding: "14px 16px",
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255,255,255,0.85)",
                            fontSize: "1.5rem",
                            mb: 0.5,
                            fontWeight: "600",
                          }}
                        >
                          Deposit
                        </Typography>
                        <Typography
                          sx={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            fontSize: "2rem",
                          }}
                        >
                          {trader.depositCommission?.toFixed(1) || "0"}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: "2px",
                          height: "50px",
                          backgroundColor: "rgba(255,255,255,0.3)",
                        }}
                      />
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255,255,255,0.85)",
                            fontSize: "1.5rem",
                            mb: 0.5,
                            fontWeight: "600",
                          }}
                        >
                          Withdrawal
                        </Typography>
                        <Typography
                          sx={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            fontSize: "2rem",
                          }}
                        >
                          {trader.withdrawalCommission?.toFixed(1) || "0"}%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!isLoading && authorizedTraders.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ color: "#B0BEC5", mb: 2 }}>
              No authorized traders found
            </Typography>
            <Typography variant="body2" sx={{ color: "#757575" }}>
              Please contact an administrator to grant you access
            </Typography>
          </Box>
        )}
      </div>
    );
  }

  // Pending Deposits View
  if (viewMode === "pending-deposits") {
    return (
      <div className="trader-kassa-management-view">
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={handleBackToPendingList}
            sx={{
              backgroundColor: "#3A4663",
              color: "#E0E6ED",
              "&:hover": { backgroundColor: "#4A5673" },
            }}
          >
            <FaArrowLeft size={20} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ color: "#E0E6ED", mb: 0.5 }}>
              Pending Trader Deposits
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#B0BEC5" }}>
              Review and approve trader deposit requests
            </Typography>
          </Box>
          <IconButton
            onClick={() => dispatch(fetchPendingTraderDeposits())}
            sx={{
              backgroundColor: "#3A4663",
              color: "#E0E6ED",
              "&:hover": {
                backgroundColor: "#4A5673",
                transform: "rotate(180deg)",
              },
              transition: "all 0.5s ease",
            }}
          >
            <FaSyncAlt size={20} />
          </IconButton>
        </Box>

        <Card
          sx={{
            backgroundColor: "#2E3B55",
            borderRadius: "16px",
            minHeight: "600px",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    border: "6px solid rgba(244, 67, 54, 0.2)",
                    borderTop: "6px solid #F44336",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: "#B0BEC5",
                    fontWeight: "500",
                    textAlign: "center",
                  }}
                >
                  Loading pending deposits...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#94a3b8",
                    fontSize: "1.3rem",
                    textAlign: "center",
                  }}
                >
                  Checking for trader deposit requests
                </Typography>
              </Box>
            ) : pendingDeposits.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  minHeight: "400px",
                }}
              >
                <FaCheckCircle
                  size={64}
                  color="#4CAF50"
                  style={{ marginBottom: "16px" }}
                />
                <Typography variant="h5" sx={{ color: "#4CAF50", mb: 1 }}>
                  All Clear!
                </Typography>
                <Typography variant="body1" sx={{ color: "#B0BEC5" }}>
                  No pending deposit requests at the moment
                </Typography>
              </Box>
            ) : (
              <DataGrid
                dataSource={pendingDeposits}
                keyExpr="_id"
                showBorders={true}
                showRowLines={true}
                showColumnLines={true}
                rowAlternationEnabled={true}
                className="trader-kassa-grid"
                height="600px"
                width="100%"
                remoteOperations={false}
                noDataText="No pending deposits"
              >
                <Paging enabled={true} pageSize={20} />

                <Column
                  caption="Trader"
                  dataField="traderName"
                  alignment="center"
                  minWidth={150}
                  cellRender={(data) => (
                    <Chip
                      label={data.value}
                      sx={{
                        backgroundColor: "#667eea",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "1.2rem",
                      }}
                    />
                  )}
                />
                <Column
                  caption="Amount (USDT)"
                  dataField="amount"
                  alignment="center"
                  minWidth={150}
                  cellRender={(data) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <FaDollarSign size={18} color="#4CAF50" />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: "#4CAF50",
                          fontSize: "1.4rem",
                        }}
                      >
                        {data.value.toFixed(2)} USDT
                      </Typography>
                    </Box>
                  )}
                />
                <Column
                  caption="Wallet Address"
                  dataField="walletAddress"
                  alignment="center"
                  minWidth={250}
                  cellRender={(data) => (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#E0E6ED",
                        fontFamily: "monospace",
                        fontSize: "1.2rem",
                      }}
                    >
                      {data.value
                        ? `${data.value.slice(0, 8)}...${data.value.slice(-8)}`
                        : "-"}
                    </Typography>
                  )}
                />
                <Column
                  caption="TxID"
                  dataField="binanceTxId"
                  alignment="center"
                  minWidth={200}
                  cellRender={(data) => (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#B0BEC5",
                        fontFamily: "monospace",
                        fontSize: "1.1rem",
                      }}
                    >
                      {data.value || "N/A"}
                    </Typography>
                  )}
                />
                <Column
                  caption="Submitted By"
                  dataField="createdBy.name"
                  alignment="center"
                  minWidth={150}
                  cellRender={(data) => (
                    <Typography
                      variant="body2"
                      sx={{ color: "#E0E6ED", fontSize: "1.3rem" }}
                    >
                      {data.value || "Unknown"}
                    </Typography>
                  )}
                />
                <Column
                  caption="Date"
                  dataField="createdAt"
                  alignment="center"
                  dataType="datetime"
                  format="dd/MM/yyyy HH:mm"
                  minWidth={180}
                />
                <Column
                  caption="Action"
                  alignment="center"
                  minWidth={200}
                  cellRender={(data) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FaCheckCircle />}
                        onClick={() => handleApproveDeposit(data.data)}
                        sx={{
                          backgroundColor: "#4CAF50",
                          "&:hover": { backgroundColor: "#45A049" },
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          px: 2,
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FaTimes />}
                        onClick={() => handleRejectDeposit(data.data)}
                        sx={{
                          backgroundColor: "#F44336",
                          "&:hover": { backgroundColor: "#D32F2F" },
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          px: 2,
                        }}
                      >
                        Reject
                      </Button>
                    </Box>
                  )}
                />
              </DataGrid>
            )}
          </CardContent>
        </Card>

        {/* Approve Modal */}
        <Dialog
          open={approveModalOpen}
          onClose={() => setApproveModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#2E3B55",
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "#E0E6ED",
              fontSize: "2.4rem",
              fontWeight: "bold",
              pb: 2,
            }}
          >
            Approve Trader Deposit
          </DialogTitle>
          <Divider sx={{ borderColor: "#3A4663" }} />
          <DialogContent sx={{ pt: 3 }}>
            {selectedDeposit && (
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    backgroundColor: "#3A4663",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Trader:{" "}
                    <strong style={{ color: "#E0E6ED", fontSize: "1.7rem" }}>
                      {selectedDeposit.traderName}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Amount:{" "}
                    <strong style={{ color: "#4CAF50", fontSize: "1.8rem" }}>
                      {selectedDeposit.amount.toFixed(2)} USDT
                    </strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#94a3b8", fontSize: "1.4rem" }}
                  >
                    Date: {new Date(selectedDeposit.createdAt).toLocaleString()}
                  </Typography>
                </Box>

                <TextField
                  label="Exchange Rate / Multiplier"
                  type="number"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  fullWidth
                  required
                  helperText={`Calculated AZN: ${
                    exchangeRate
                      ? (
                          selectedDeposit.amount * parseFloat(exchangeRate)
                        ).toFixed(2)
                      : "0.00"
                  } AZN`}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-input": {
                      fontSize: "1.8rem",
                      color: "#E0E6ED",
                      padding: "16px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "1.5rem",
                      color: "#FF9800",
                      fontWeight: "bold",
                      marginTop: "8px",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RiExchangeDollarFill color="#556CD6" size={28} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Notes (Optional)"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any notes about this approval..."
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "1.5rem",
                      color: "#E0E6ED",
                      padding: "12px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setApproveModalOpen(false)}
              sx={{
                color: "#B0BEC5",
                fontSize: "1.5rem",
                py: 1.5,
                px: 3,
                "&:hover": { backgroundColor: "#3A4663" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              variant="contained"
              disabled={!exchangeRate}
              sx={{
                backgroundColor: "#4CAF50",
                fontSize: "1.5rem",
                fontWeight: "bold",
                py: 1.5,
                px: 5,
                "&:hover": { backgroundColor: "#45A049" },
                "&:disabled": { backgroundColor: "#666", color: "#999" },
              }}
            >
              Approve & Credit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Modal */}
        <Dialog
          open={rejectModalOpen}
          onClose={() => setRejectModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#2E3B55",
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "#E0E6ED",
              fontSize: "2.4rem",
              fontWeight: "bold",
              pb: 2,
            }}
          >
            Reject Trader Deposit
          </DialogTitle>
          <Divider sx={{ borderColor: "#3A4663" }} />
          <DialogContent sx={{ pt: 3 }}>
            {selectedDeposit && (
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    backgroundColor: "#3A4663",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Trader:{" "}
                    <strong style={{ color: "#E0E6ED", fontSize: "1.7rem" }}>
                      {selectedDeposit.traderName}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Amount:{" "}
                    <strong style={{ color: "#F44336", fontSize: "1.8rem" }}>
                      {selectedDeposit.amount.toFixed(2)} USDT
                    </strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#94a3b8", fontSize: "1.4rem" }}
                  >
                    Date: {new Date(selectedDeposit.createdAt).toLocaleString()}
                  </Typography>
                </Box>

                <TextField
                  label="Rejection Reason (Optional)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="e.g., Invalid transaction ID, duplicate request, etc."
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "1.5rem",
                      color: "#E0E6ED",
                      padding: "12px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setRejectModalOpen(false)}
              sx={{
                color: "#B0BEC5",
                fontSize: "1.5rem",
                py: 1.5,
                px: 3,
                "&:hover": { backgroundColor: "#3A4663" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              variant="contained"
              sx={{
                backgroundColor: "#F44336",
                fontSize: "1.5rem",
                fontWeight: "bold",
                py: 1.5,
                px: 5,
                "&:hover": { backgroundColor: "#D32F2F" },
              }}
            >
              Reject Deposit
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Pending Withdrawals View
  if (viewMode === "pending-withdrawals") {
    return (
      <div className="trader-kassa-management-view">
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={handleBackToPendingList}
            sx={{
              backgroundColor: "#3A4663",
              color: "#E0E6ED",
              "&:hover": { backgroundColor: "#4A5673" },
            }}
          >
            <FaArrowLeft size={20} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ color: "#E0E6ED", mb: 0.5 }}>
              Pending Trader Withdrawals
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#B0BEC5" }}>
              Review and approve trader withdrawal requests
            </Typography>
          </Box>
          <IconButton
            onClick={() => dispatch(fetchPendingTraderWithdrawals())}
            sx={{
              backgroundColor: "#3A4663",
              color: "#E0E6ED",
              "&:hover": {
                backgroundColor: "#4A5673",
                transform: "rotate(180deg)",
              },
              transition: "all 0.5s ease",
            }}
          >
            <FaSyncAlt size={20} />
          </IconButton>
        </Box>

        <Card
          sx={{
            backgroundColor: "#2E3B55",
            borderRadius: "16px",
            minHeight: "600px",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    border: "6px solid rgba(156, 39, 176, 0.2)",
                    borderTop: "6px solid #9C27B0",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: "#B0BEC5",
                    fontWeight: "500",
                    textAlign: "center",
                  }}
                >
                  Loading pending withdrawals...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#94a3b8",
                    fontSize: "1.3rem",
                    textAlign: "center",
                  }}
                >
                  Checking for trader withdrawal requests
                </Typography>
              </Box>
            ) : pendingWithdrawals.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  minHeight: "400px",
                }}
              >
                <FaCheckCircle
                  size={64}
                  color="#4CAF50"
                  style={{ marginBottom: "16px" }}
                />
                <Typography variant="h5" sx={{ color: "#4CAF50", mb: 1 }}>
                  All Clear!
                </Typography>
                <Typography variant="body1" sx={{ color: "#B0BEC5" }}>
                  No pending withdrawal requests at the moment
                </Typography>
              </Box>
            ) : (
              <DataGrid
                dataSource={pendingWithdrawals}
                keyExpr="_id"
                showBorders={true}
                showRowLines={true}
                showColumnLines={true}
                rowAlternationEnabled={true}
                className="trader-kassa-grid"
                height="600px"
                width="100%"
                remoteOperations={false}
                noDataText="No pending withdrawals"
              >
                <Column
                  caption="Trader"
                  dataField="traderName"
                  alignment="center"
                  minWidth={150}
                />
                <Column
                  caption="Amount"
                  dataField="amount"
                  alignment="center"
                  minWidth={180}
                  cellRender={(data) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {data.data.currency === "USDT" ? (
                        <>
                          <FaDollarSign size={18} color="#4CAF50" />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: "#4CAF50",
                              fontSize: "1.4rem",
                            }}
                          >
                            {data.value.toFixed(2)} USDT
                          </Typography>
                        </>
                      ) : (
                        <>
                          <img
                            src={aznRedIcon}
                            alt="AZN"
                            style={{ width: "20px", height: "20px" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: "#F44336",
                              fontSize: "1.4rem",
                            }}
                          >
                            {data.value.toFixed(2)} AZN
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                />
                <Column
                  caption="Wallet Address"
                  dataField="walletAddress"
                  alignment="center"
                  minWidth={250}
                  cellRender={(data) => (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#E0E6ED",
                        fontFamily: "monospace",
                        fontSize: "1.2rem",
                      }}
                    >
                      {data.value
                        ? `${data.value.slice(0, 8)}...${data.value.slice(-8)}`
                        : "-"}
                    </Typography>
                  )}
                />
                <Column
                  caption="Network"
                  dataField="network"
                  alignment="center"
                  minWidth={100}
                />
                <Column
                  caption="Created"
                  dataField="createdAt"
                  alignment="center"
                  dataType="datetime"
                  format="dd/MM/yyyy HH:mm"
                  minWidth={180}
                />
                <Column
                  caption="Action"
                  alignment="center"
                  minWidth={200}
                  cellRender={(data) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FaCheckCircle />}
                        onClick={() => handleApproveWithdrawal(data.data)}
                        sx={{
                          backgroundColor: "#4CAF50",
                          "&:hover": { backgroundColor: "#45A049" },
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          px: 2,
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FaTimes />}
                        onClick={() => handleRejectWithdrawal(data.data)}
                        sx={{
                          backgroundColor: "#F44336",
                          "&:hover": { backgroundColor: "#D32F2F" },
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          px: 2,
                        }}
                      >
                        Reject
                      </Button>
                    </Box>
                  )}
                />
              </DataGrid>
            )}
          </CardContent>
        </Card>

        {/* Approve Withdrawal Modal */}
        <Dialog
          open={approveWithdrawalModalOpen}
          onClose={() => setApproveWithdrawalModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#2E3B55",
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "#E0E6ED",
              fontSize: "2.4rem",
              fontWeight: "bold",
              pb: 2,
            }}
          >
            Approve Trader Withdrawal
          </DialogTitle>
          <Divider sx={{ borderColor: "#3A4663" }} />
          <DialogContent sx={{ pt: 3 }}>
            {selectedWithdrawal && (
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    backgroundColor: "#3A4663",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Trader:{" "}
                    <strong style={{ color: "#E0E6ED", fontSize: "1.7rem" }}>
                      {selectedWithdrawal.traderName}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Amount:{" "}
                    <strong
                      style={{
                        color:
                          selectedWithdrawal.currency === "USDT"
                            ? "#4CAF50"
                            : "#F44336",
                        fontSize: "1.8rem",
                      }}
                    >
                      {selectedWithdrawal.amount.toFixed(2)}{" "}
                      {selectedWithdrawal.currency}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.5rem" }}
                  >
                    Wallet:{" "}
                    <strong
                      style={{
                        color: "#94a3b8",
                        fontSize: "1.3rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {selectedWithdrawal.walletAddress}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#94a3b8", fontSize: "1.4rem" }}
                  >
                    Date:{" "}
                    {new Date(selectedWithdrawal.createdAt).toLocaleString()}
                  </Typography>
                </Box>

                <TextField
                  label="Exchange Rate / Multiplier"
                  type="number"
                  value={withdrawalExchangeRate}
                  onChange={(e) => setWithdrawalExchangeRate(e.target.value)}
                  fullWidth
                  required
                  helperText={
                    selectedWithdrawal.currency === "USDT"
                      ? `AZN deducted: ${
                          withdrawalExchangeRate
                            ? (
                                selectedWithdrawal.amount *
                                parseFloat(withdrawalExchangeRate)
                              ).toFixed(2)
                            : "0.00"
                        } AZN`
                      : `USDT sent: ${
                          withdrawalExchangeRate
                            ? (
                                selectedWithdrawal.amount /
                                parseFloat(withdrawalExchangeRate)
                              ).toFixed(2)
                            : "0.00"
                        } USDT`
                  }
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-input": {
                      fontSize: "1.8rem",
                      color: "#E0E6ED",
                      padding: "16px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "1.5rem",
                      color: "#FF9800",
                      fontWeight: "bold",
                      marginTop: "8px",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RiExchangeDollarFill color="#556CD6" size={28} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Transaction ID (Optional)"
                  value={withdrawalTxId}
                  onChange={(e) => setWithdrawalTxId(e.target.value)}
                  fullWidth
                  placeholder="0x..."
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-input": {
                      fontSize: "1.5rem",
                      color: "#E0E6ED",
                      padding: "12px",
                      fontFamily: "monospace",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                  }}
                />

                <TextField
                  label="Notes (Optional)"
                  value={withdrawalNotes}
                  onChange={(e) => setWithdrawalNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any notes about this withdrawal..."
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "1.5rem",
                      color: "#E0E6ED",
                      padding: "12px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setApproveWithdrawalModalOpen(false)}
              sx={{
                color: "#B0BEC5",
                fontSize: "1.5rem",
                py: 1.5,
                px: 3,
                "&:hover": { backgroundColor: "#3A4663" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveWithdrawalConfirm}
              variant="contained"
              disabled={!withdrawalExchangeRate}
              sx={{
                backgroundColor: "#4CAF50",
                fontSize: "1.5rem",
                fontWeight: "bold",
                py: 1.5,
                px: 5,
                "&:hover": { backgroundColor: "#45A049" },
                "&:disabled": { backgroundColor: "#666", color: "#999" },
              }}
            >
              Approve & Deduct
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Withdrawal Modal */}
        <Dialog
          open={rejectWithdrawalModalOpen}
          onClose={() => setRejectWithdrawalModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#2E3B55",
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "#E0E6ED",
              fontSize: "2.4rem",
              fontWeight: "bold",
              pb: 2,
            }}
          >
            Reject Trader Withdrawal
          </DialogTitle>
          <Divider sx={{ borderColor: "#3A4663" }} />
          <DialogContent sx={{ pt: 3 }}>
            {selectedWithdrawal && (
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    backgroundColor: "#3A4663",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Trader:{" "}
                    <strong style={{ color: "#E0E6ED", fontSize: "1.7rem" }}>
                      {selectedWithdrawal.traderName}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#B0BEC5", mb: 2, fontSize: "1.6rem" }}
                  >
                    Amount:{" "}
                    <strong style={{ color: "#F44336", fontSize: "1.8rem" }}>
                      {selectedWithdrawal.amount.toFixed(2)}{" "}
                      {selectedWithdrawal.currency}
                    </strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#94a3b8", fontSize: "1.4rem" }}
                  >
                    Date:{" "}
                    {new Date(selectedWithdrawal.createdAt).toLocaleString()}
                  </Typography>
                </Box>

                <TextField
                  label="Rejection Reason (Optional)"
                  value={withdrawalRejectReason}
                  onChange={(e) => setWithdrawalRejectReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="e.g., Invalid wallet address, insufficient balance, etc."
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "1.5rem",
                      color: "#E0E6ED",
                      padding: "12px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#B0BEC5",
                    },
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setRejectWithdrawalModalOpen(false)}
              sx={{
                color: "#B0BEC5",
                fontSize: "1.5rem",
                py: 1.5,
                px: 3,
                "&:hover": { backgroundColor: "#3A4663" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectWithdrawalConfirm}
              variant="contained"
              sx={{
                backgroundColor: "#F44336",
                fontSize: "1.5rem",
                fontWeight: "bold",
                py: 1.5,
                px: 5,
                "&:hover": { backgroundColor: "#D32F2F" },
              }}
            >
              Reject Withdrawal
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Trader Detail View - Loading State
  if (isLoading || !traderDetail) {
    return (
      <div className="trader-kassa-management-view">
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={handleBackToList}
            sx={{
              backgroundColor: "#3A4663",
              color: "#E0E6ED",
              "&:hover": { backgroundColor: "#4A5673" },
            }}
          >
            <FaArrowLeft size={20} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ color: "#E0E6ED", mb: 0.5 }}>
              Loading Trader Details...
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#B0BEC5" }}>
              Please wait
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: 3,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              border: "6px solid rgba(102, 126, 234, 0.2)",
              borderTop: "6px solid #667eea",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: "#B0BEC5",
              fontWeight: "500",
              textAlign: "center",
            }}
          >
            Loading trader information...
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#94a3b8",
              fontSize: "1.3rem",
              textAlign: "center",
            }}
          >
            Fetching balance, commissions, and transaction history
          </Typography>
        </Box>
      </div>
    );
  }

  // Trader Detail View
  return (
    <div className="trader-kassa-management-view">
      {/* Back Button & Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton
          onClick={handleBackToList}
          sx={{
            backgroundColor: "#3A4663",
            color: "#E0E6ED",
            "&:hover": { backgroundColor: "#4A5673" },
          }}
        >
          <FaArrowLeft size={20} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ color: "#E0E6ED", mb: 0.5 }}>
            {traderDetail?.traderName}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "#B0BEC5" }}>
            {traderDetail?.traderVersion} • Trader Management
          </Typography>
        </Box>
        <IconButton
          onClick={handleRefresh}
          sx={{
            backgroundColor: "#3A4663",
            color: "#E0E6ED",
            "&:hover": {
              backgroundColor: "#4A5673",
              transform: "rotate(180deg)",
            },
            transition: "all 0.5s ease",
          }}
        >
          <FaSyncAlt size={20} />
        </IconButton>
      </Box>

      {/* Balance & Commission Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Balance Card */}
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "16px",
              height: "100%",
              minHeight: "260px",
            }}
          >
            <CardContent
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "20px",
                  padding: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <MdAccountBalanceWallet size={64} color="#ffffff" />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  mb: 1.5,
                  fontSize: "1.4rem",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                }}
              >
                Current Balance
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "3.5rem",
                  lineHeight: 1.2,
                }}
              >
                {traderDetail?.balance?.toFixed(2) || "0.00"}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "1.8rem",
                  fontWeight: "500",
                  mt: 0.5,
                }}
              >
                AZN
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Commission Card */}
        <Grid item xs={12} md={4.5}>
          <Card
            sx={{
              backgroundColor: "#1e293b",
              borderRadius: "16px",
              minHeight: "260px",
              height: "100%",
              border: "1px solid #334155",
            }}
          >
            <CardContent
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <RiExchangeDollarFill size={40} color="#3b82f6" />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#f1f5f9",
                      fontWeight: "bold",
                      fontSize: "1.8rem",
                    }}
                  >
                    Commission Rates
                  </Typography>
                </Box>
                {!isEditingCommission ? (
                  <IconButton
                    onClick={handleEditCommission}
                    sx={{
                      backgroundColor: "#556CD6",
                      color: "white",
                      width: 42,
                      height: 42,
                      "&:hover": { backgroundColor: "#4A5BC4" },
                    }}
                  >
                    <FaEdit size={20} />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleSaveCommission}
                    sx={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      width: 42,
                      height: 42,
                      "&:hover": { backgroundColor: "#45A049" },
                    }}
                  >
                    <FaSave size={20} />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2} sx={{ flex: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Deposit (%)"
                    type="number"
                    value={depositCommission}
                    onChange={(e) => setDepositCommission(e.target.value)}
                    disabled={!isEditingCommission}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ color: "#E0E6ED" }}
                        >
                          %
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "#0f172a",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "2rem",
                        color: "#E0E6ED",
                        fontWeight: "600",
                        padding: "14px 16px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "1.4rem",
                        color: "#94a3b8",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#475569",
                      },
                      "& .Mui-disabled": {
                        backgroundColor: "#1e293b",
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#94a3b8",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Withdrawal (%)"
                    type="number"
                    value={withdrawalCommission}
                    onChange={(e) => setWithdrawalCommission(e.target.value)}
                    disabled={!isEditingCommission}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ color: "#E0E6ED" }}
                        >
                          %
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "#0f172a",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "2rem",
                        color: "#E0E6ED",
                        fontWeight: "600",
                        padding: "14px 16px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "1.4rem",
                        color: "#94a3b8",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#475569",
                      },
                      "& .Mui-disabled": {
                        backgroundColor: "#1e293b",
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#94a3b8",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* TRC Address Card */}
        <Grid item xs={12} md={4.5}>
          <Card
            sx={{
              backgroundColor: "#1e293b",
              borderRadius: "16px",
              minHeight: "260px",
              height: "100%",
              border: "1px solid #334155",
            }}
          >
            <CardContent
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <TbWorld size={40} color="#3b82f6" />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#f1f5f9",
                      fontWeight: "bold",
                      fontSize: "1.8rem",
                    }}
                  >
                    USDT Address
                  </Typography>
                </Box>
                {!isEditingTRC ? (
                  <IconButton
                    onClick={handleEditTRC}
                    sx={{
                      backgroundColor: "#556CD6",
                      color: "white",
                      width: 42,
                      height: 42,
                      "&:hover": { backgroundColor: "#4A5BC4" },
                    }}
                  >
                    <FaEdit size={20} />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleSaveTRC}
                    sx={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      width: 42,
                      height: 42,
                      "&:hover": { backgroundColor: "#45A049" },
                    }}
                  >
                    <FaSave size={20} />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <TextField
                  label="USDT Wallet Address"
                  value={trcAddress}
                  onChange={(e) => setTrcAddress(e.target.value)}
                  disabled={!isEditingTRC}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter TRC20 wallet address..."
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "#0f172a",
                      borderRadius: "8px",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "1.6rem",
                      color: "#E0E6ED",
                      fontFamily: "monospace",
                      fontWeight: "600",
                      padding: "14px 16px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#94a3b8",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#475569",
                    },
                    "& .Mui-disabled": {
                      backgroundColor: "#1e293b",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#94a3b8",
                    },
                  }}
                />

                <TextField
                  label="Network"
                  value={trcNetwork}
                  onChange={(e) => setTrcNetwork(e.target.value)}
                  disabled={!isEditingTRC}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "#0f172a",
                      borderRadius: "8px",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "2rem",
                      color: "#E0E6ED",
                      fontWeight: "600",
                      padding: "14px 16px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "1.4rem",
                      color: "#94a3b8",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#475569",
                    },
                    "& .Mui-disabled": {
                      backgroundColor: "#1e293b",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#94a3b8",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Tables */}
      <Card
        sx={{
          backgroundColor: "#2E3B55",
          borderRadius: "16px",
          minHeight: "500px",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
              borderBottom: "2px solid #3A4663",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#556CD6",
                  height: "3px",
                },
                "& .MuiTab-root": {
                  color: "#B0BEC5",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  textTransform: "none",
                  minHeight: "56px",
                  "&.Mui-selected": {
                    color: "#556CD6",
                  },
                  "& .MuiTab-iconWrapper": {
                    marginRight: "10px",
                  },
                },
              }}
            >
              <Tab
                icon={<FaWallet size={22} />}
                iconPosition="start"
                label="Wallet History"
              />
              <Tab
                icon={<BiTransfer size={22} />}
                iconPosition="start"
                label="Transaction History"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {activeTab === 0 && (
              <Box sx={{ height: "500px" }}>
                {!isLoading && (
                  <DataGrid
                    dataSource={[
                      ...cryptoDeposits
                        .filter((d) => d && d._id)
                        .map((d) => ({ ...d, type: "deposit" })),
                      ...withdrawals
                        .filter((w) => w && w._id)
                        .map((w) => ({ ...w, type: "withdrawal" })),
                    ].sort(
                      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                    )}
                    keyExpr="_id"
                    showBorders={true}
                    showRowLines={true}
                    showColumnLines={true}
                    rowAlternationEnabled={true}
                    className="trader-wallet-grid"
                    height="100%"
                    width="100%"
                    remoteOperations={false}
                    noDataText="No transaction history available"
                  >
                    <Paging enabled={true} pageSize={50} />

                    <Column
                      dataField="type"
                      caption="Type"
                      cellRender={(cellData) => {
                        const row = cellData.row.data;
                        const isCanceled = row.status === "canceled";

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={
                                cellData.value === "deposit"
                                  ? "Deposit"
                                  : "Withdrawal"
                              }
                              size="small"
                              sx={{
                                backgroundColor: isCanceled
                                  ? "#757575"
                                  : cellData.value === "deposit"
                                  ? "#4CAF50"
                                  : "#F44336",
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "1rem",
                                minWidth: "110px",
                                px: 1,
                              }}
                            />
                          </Box>
                        );
                      }}
                      minWidth={150}
                      alignment="center"
                    />
                    <Column
                      dataField="amount"
                      caption="Amount"
                      cellRender={renderAmountCell}
                      minWidth={320}
                      alignment="center"
                    />
                    <Column
                      dataField="status"
                      caption="Status"
                      cellRender={renderStatusCell}
                      minWidth={200}
                      alignment="center"
                    />
                    <Column
                      dataField="exchangeRate"
                      caption="Exchange Rate"
                      cellRender={renderExchangeRateCell}
                      minWidth={120}
                      alignment="center"
                    />
                    <Column
                      dataField="binanceTxId"
                      caption="Transaction ID"
                      minWidth={125}
                      alignment="center"
                      cellRender={(cellData) => {
                        const row = cellData.row.data;
                        const isCanceled = row.status === "canceled";
                        return (
                          <span
                            style={{
                              color: isCanceled ? "#757575" : "#E0E6ED",
                            }}
                          >
                            {cellData.value || "-"}
                          </span>
                        );
                      }}
                    />
                    <Column
                      dataField="createdAt"
                      caption="Created"
                      dataType="datetime"
                      format="dd/MM/yyyy HH:mm"
                      minWidth={200}
                      alignment="center"
                      cssClass="date-column"
                      cellRender={(cellData) => {
                        const row = cellData.row.data;
                        const isCanceled = row.status === "canceled";
                        const date = cellData.value
                          ? new Date(cellData.value).toLocaleString()
                          : "-";
                        return (
                          <span
                            style={{
                              color: isCanceled ? "#757575" : "#E0E6ED",
                            }}
                          >
                            {date}
                          </span>
                        );
                      }}
                    />
                    <Column
                      dataField="confirmedAt"
                      caption="Confirmed"
                      dataType="datetime"
                      format="dd/MM/yyyy HH:mm"
                      minWidth={200}
                      alignment="center"
                      cssClass="date-column"
                      cellRender={(cellData) => {
                        const row = cellData.row.data;
                        const isCanceled = row.status === "canceled";
                        const date = cellData.value
                          ? new Date(cellData.value).toLocaleString()
                          : "-";
                        return (
                          <span
                            style={{
                              color: isCanceled ? "#757575" : "#E0E6ED",
                            }}
                          >
                            {date}
                          </span>
                        );
                      }}
                    />
                  </DataGrid>
                )}
                {isLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#B0BEC5" }}>
                      Loading wallet history...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ height: "500px" }}>
                {!isLoading && Array.isArray(balanceHistory) && (
                  <DataGrid
                    dataSource={balanceHistory.filter(
                      (h) => h && h.transactionId
                    )}
                    keyExpr="transactionId"
                    showBorders={true}
                    showRowLines={true}
                    showColumnLines={true}
                    rowAlternationEnabled={true}
                    className="trader-wallet-grid"
                    height="100%"
                    width="100%"
                    remoteOperations={false}
                    noDataText="No transaction history available"
                  >
                    <Paging enabled={true} pageSize={50} />

                    <Column
                      dataField="type"
                      caption="Type"
                      cellRender={renderHistoryTypeCell}
                      minWidth={150}
                      alignment="center"
                    />
                    <Column
                      dataField="amount"
                      caption="Amount"
                      cellRender={renderHistoryAmountCell}
                      minWidth={180}
                      alignment="center"
                    />
                    <Column
                      dataField="commissionRate"
                      caption="Commission Rate"
                      minWidth={150}
                      alignment="center"
                      cellRender={(cellData) => {
                        const rate = cellData.value || 0;
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "bold",
                                color: "#FF9800",
                                fontSize: "1.4rem",
                              }}
                            >
                              - {rate.toFixed(2)} %
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Column
                      dataField="commissionFee"
                      caption="Commission Fee"
                      minWidth={180}
                      alignment="center"
                      cellRender={(cellData) => {
                        const fee = cellData.value || 0;
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "bold",
                                color: "#FF9800",
                                fontSize: "1.4rem",
                              }}
                            >
                              - {fee.toFixed(2)} AZN
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Column
                      dataField="balanceBefore"
                      caption="Balance Before"
                      minWidth={180}
                      alignment="center"
                      cellRender={(cellData) => {
                        const balance = cellData.value || 0;
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "600",
                                color: "#B0BEC5",
                                fontSize: "1.4rem",
                              }}
                            >
                              {balance.toFixed(2)} AZN
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Column
                      dataField="balanceAfter"
                      caption="Balance After"
                      minWidth={180}
                      alignment="center"
                      cellRender={(cellData) => {
                        const balance = cellData.value || 0;
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "700",
                                color: "#4CAF50",
                                fontSize: "1.5rem",
                              }}
                            >
                              {balance.toFixed(2)} AZN
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Column
                      dataField="transactionId"
                      caption="Transaction ID"
                      minWidth={250}
                      alignment="center"
                    />
                  </DataGrid>
                )}
                {isLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#B0BEC5" }}>
                      Loading history...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default TraderKassaManagementView;
